import { useEffect, useState } from 'react';
import { RoomSnapshot } from '../room/types';
import type { PackQuestion } from '../pack/types';

interface PromptText { text?: string }
interface AnswerRow  { id: string; text: string; media?: { type: string; url: string; alt_text?: string } }

function promptText(q: PackQuestion | undefined): string {
  if (!q) return '';
  const p = q.prompt as PromptText | string | null | undefined;
  if (typeof p === 'string') return p;
  return p?.text ?? '';
}

function answers(q: PackQuestion | undefined): AnswerRow[] {
  if (!q) return [];
  return (q.answers as AnswerRow[] | undefined) ?? [];
}

export function QuestionScreen({
  snapshot,
  questionsById,
}: {
  snapshot: RoomSnapshot;
  questionsById: Map<string, PackQuestion>;
}) {
  const total = snapshot.timeLimitMs ?? 0;
  const start = snapshot.roundStartedAtMs ?? Date.now();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(t);
  }, []);

  const remaining = Math.max(0, total - (now - start));
  const pct = total > 0 ? Math.max(0, remaining / total) : 0;

  const q       = snapshot.currentQuestionId ? questionsById.get(snapshot.currentQuestionId) : undefined;
  const text    = promptText(q);
  const choices = answers(q);

  return (
    <div className="app fade-in">
      <div className="row" style={{ alignItems: 'center' }}>
        <div style={{ fontSize: 22, color: 'var(--c-text-muted)' }}>
          Round {snapshot.currentRoundIndex + 1}
        </div>
        <div className="spacer" />
        <div style={{ fontSize: 32, color: 'var(--c-accent)', fontWeight: 700 }}>
          {Math.ceil(remaining / 1000)}s
        </div>
      </div>

      <div style={{
        height: 6, borderRadius: 'var(--r-pill)',
        background: 'var(--c-elevated)', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', width: `${pct * 100}%`,
          background: 'var(--c-accent)',
          transition: 'width 120ms linear',
        }} />
      </div>

      <div className="center spacer">
        <div className="question-tv">
          {text || snapshot.currentQuestionId || 'Loading…'}
        </div>
      </div>

      {choices.length > 0 && (
        <div className="row" style={{ flexWrap: 'wrap', gap: 16 }}>
          {choices.map((a) => (
            <div className="player-card" key={a.id} style={{ minWidth: 220 }}>
              {a.media?.url && (
                <img
                  src={a.media.url}
                  alt={a.media.alt_text ?? ''}
                  style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8 }}
                />
              )}
              <div style={{ fontWeight: 600, fontSize: 22 }}>{a.text}</div>
            </div>
          ))}
        </div>
      )}

      <div className="row">
        {snapshot.players.map((p) => (
          <div className="player-card" key={p.playerId}>
            <div className="avatar" aria-hidden>👤</div>
            <div>
              <div style={{ fontWeight: 600 }}>{p.displayName}</div>
            </div>
            <div className="score">{p.score}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
